function horizontalBoxRow(pixels: Uint8ClampedArray, width: number, height: number, y: number, channel: number, radius: number): Uint8Array {
  const row = Math.max(0, Math.min(height - 1, y));
  const output = new Uint8Array(width);
  const sample = (x: number) => pixels[(row * width + Math.max(0, Math.min(width - 1, x))) * 4 + channel] ?? 0;
  let sum = 0;
  for (let x = -radius; x <= radius; x += 1) sum += sample(x);
  const window = radius * 2 + 1;
  for (let x = 0; x < width; x += 1) {
    output[x] = Math.round(sum / window);
    sum += sample(x + radius + 1) - sample(x - radius);
  }
  return output;
}

/**
 * Classic neutral-gray High Pass using a separable edge-clamped box low-pass.
 * The row ring keeps memory proportional to width × radius, not image area.
 */
export function applyHighPassPixels(pixels: Uint8ClampedArray, width: number, height: number, radius: number): void {
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1 || pixels.length !== width * height * 4) {
    throw new Error('High Pass pixel dimensions are invalid.');
  }
  const blurRadius = Math.max(1, Math.min(64, Math.round(radius)));
  const window = blurRadius * 2 + 1;
  for (let channel = 0; channel < 3; channel += 1) {
    const rows: Uint8Array[] = [];
    const vertical = new Uint32Array(width);
    for (let sourceY = -blurRadius; sourceY <= blurRadius; sourceY += 1) {
      const row = horizontalBoxRow(pixels, width, height, sourceY, channel, blurRadius);
      rows.push(row);
      for (let x = 0; x < width; x += 1) vertical[x] = (vertical[x] ?? 0) + (row[x] ?? 0);
    }
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const offset = (y * width + x) * 4 + channel;
        const lowPass = (vertical[x] ?? 0) / window;
        pixels[offset] = Math.round(Math.max(0, Math.min(255, 128 + (pixels[offset] ?? 0) - lowPass)));
      }
      const removed = rows.shift();
      const added = horizontalBoxRow(pixels, width, height, y + blurRadius + 1, channel, blurRadius);
      rows.push(added);
      for (let x = 0; x < width; x += 1) vertical[x] = (vertical[x] ?? 0) - (removed?.[x] ?? 0) + (added[x] ?? 0);
    }
  }
}
